import { useMemo } from 'react';
import { toGameVariant, type GameVariant } from '@draughts/shared';
import { usePreferencesStore } from '../../store/preferences-store';
import {
  resolvePieceColors,
  colorPairFromVariant,
  variantPieceColors,
  PIECE_COLOR_THEMES,
  type ColorPair,
} from './piece-themes';
import { resolveBoardColors, type BoardColors } from './board-themes';

/**
 * I colori risolti per una scacchiera, letti dalle preferenze.
 *
 * Esistono come hook e non come chiamata diretta al resolver perché il tema
 * 'custom' ha bisogno di DUE valori in più oltre al nome del tema, e i punti che
 * disegnano una scacchiera sono sette (partita, replay, editor, anteprima puzzle,
 * anteprima impostazioni, catture, avatar). Ripetere in ognuno la lettura dei due
 * esadecimali significa che il prossimo punto che nasce se li dimentica e mostra i
 * colori della variante al posto di quelli scelti — un difetto che si vede solo
 * andando a cercarlo. Qui la combinazione sta in un posto solo.
 *
 * I due esadecimali si leggono con selettori separati e si ricompongono in
 * `useMemo`: un selettore che restituisse `{light, dark}` creerebbe un oggetto
 * nuovo a ogni render dello store e farebbe ri-renderizzare l'intera scacchiera a
 * ogni battito, che su una 10x10 a 40 pezzi non è gratis.
 */
export function usePieceColorPair(variant: GameVariant): ColorPair {
  const theme = usePreferencesStore((s) => s.pieceColorTheme);
  const light = usePreferencesStore((s) => s.customPieceLight);
  const dark = usePreferencesStore((s) => s.customPieceDark);
  return useMemo(
    () => resolvePieceColors(theme, variant, { light, dark }),
    [theme, variant, light, dark],
  );
}

/**
 * La coppia colori per un pezzo disegnato FUORI da una scacchiera — l'indicatore
 * nell'avatar, uno swatch in una riga di elenco.
 *
 * Fuori dalla scacchiera 'auto' non ha una variante addosso, ma quasi sempre ce l'ha
 * CHI DISEGNA: il segnalino dell'avatar sta accanto alla scacchiera di una partita, il
 * dischetto di una riga sta accanto alla pastiglia della variante di quella partita. Per
 * un giro 'auto' è ricaduto qui sul tema classico, e il ripiego si vedeva: un segnalino
 * crema accanto a una scacchiera di pedine oro. Quindi la variante si riceve, e il ripiego
 * resta solo per chi davvero non ne ha nessuna — ed è la variante PREFERITA, la stessa su
 * cui si disegnano le anteprime delle impostazioni, non un neutro che non esiste da
 * nessuna parte.
 *
 * Il `??` è anche una guardia: una variante indefinita — uno store mockato a metà, un
 * chiamante distratto — degrada sulla preferita invece di cercare `VARIANT_SPECS[undefined]`.
 *
 * 'custom' i suoi due colori ce li ha e va onorato comunque: è l'unico modo perché un
 * pezzo scelto a mano si veda uguale dentro e fuori dalla partita.
 */
export function useStandalonePieceColorPair(variant?: GameVariant): ColorPair {
  const theme = usePreferencesStore((s) => s.pieceColorTheme);
  const light = usePreferencesStore((s) => s.customPieceLight);
  const dark = usePreferencesStore((s) => s.customPieceDark);
  const preferredVariant = usePreferencesStore((s) => s.preferredVariant);
  return useMemo(() => {
    if (theme === 'custom') return colorPairFromVariant(light, dark);
    if (theme === 'auto') return variantPieceColors(variant ?? toGameVariant(preferredVariant));
    return PIECE_COLOR_THEMES[theme] ?? PIECE_COLOR_THEMES.classic;
  }, [theme, light, dark, variant, preferredVariant]);
}

export function useBoardColors(variant: GameVariant): BoardColors {
  const theme = usePreferencesStore((s) => s.boardTheme);
  const light = usePreferencesStore((s) => s.customBoardLight);
  const dark = usePreferencesStore((s) => s.customBoardDark);
  return useMemo(
    () => resolveBoardColors(theme, variant, { light, dark }),
    [theme, variant, light, dark],
  );
}
